/**
 * 资源类型两栏筛选（组件见 ResourceTypePicker.vue）。
 * 左栏一级：分组（音乐 / 白噪音、食谱、Workout）、stickers 或没有二级的单个类型；
 * 右栏二级：All + 分组内的类型，或 All + stickers 的四种子类型。All 与其它项互斥。
 *
 * 选中值用一个字符串表示，筛选摘要、计数、重置沿用原来的单值逻辑：
 *   没有二级的类型        'Flow'
 *   某个一级的 All        'group:recipe' / 'stickers'
 *   某个一级下选中几项    'group:recipe::["recipe_v2"]' / 'stickers::["static_pack","animated_solo"]'
 * 二级只能选同一个一级下的项；到另一个一级里选会替换原来的选择。
 * 发请求前用 toTypeQuery 翻译成接口参数 wallpaperType / wallpaperTypes / stickerKind。
 */

export const STICKER_TYPE = 'stickers'

/** 右栏 All 项的取值，不会与类型名或子类型冲突。 */
export const TYPE_ALL = '__all__'

/** 分组里列全该组的类型；下拉只显示当前有资源的那些，选 All 时按全部类型筛，下线类型的资源也能查到。 */
export const TYPE_GROUPS = [
  {
    key: 'audio',
    label: '音乐 / 白噪音',
    types: ['音乐', 'sounds(白噪音)', 'music(白噪音)', 'sleep story(白噪音)', 'meditation(白噪音)', 'ASMR(白噪音)', 'sounds_live(白噪音)'],
  },
  { key: 'recipe', label: '食谱', types: ['recipe(食谱)', 'recipe_v2'] },
  { key: 'workout', label: 'Workout', types: ['workout_action', 'workout_music', 'workout_voice', 'workout_collection'] },
]

/** 与后端 StickerKind 的参数写法一致；文案与编辑页的 stickers type 一致。 */
export const STICKER_KINDS = [
  { value: 'static_pack', label: 'static pack' },
  { value: 'animated_pack', label: 'animated pack' },
  { value: 'static_solo', label: 'static solo' },
  { value: 'animated_solo', label: 'animated solo' },
]

const GROUP_PREFIX = 'group:'
const ITEMS_MARK = '::'

/** 列表资源类型列第二行：后端 stickerKind（如 static_pack）→ 文案；不认识的值不显示。 */
export function stickerKindLabel(kind) {
  const item = STICKER_KINDS.find((entry) => entry.value === kind)
  return item ? item.label : ''
}

function groupOf(type) {
  return TYPE_GROUPS.find((group) => group.types.includes(type))
}

function groupByParent(parent) {
  return TYPE_GROUPS.find((group) => GROUP_PREFIX + group.key === parent)
}

function isParent(value) {
  return value === STICKER_TYPE || !!groupByParent(value)
}

/**
 * 左栏：能多选的（有二级）在上，按 TYPE_GROUPS 再 stickers 的固定顺序；只能单选的在下。
 * 单选类型默认按字母排、不分大小写，新类型自动落位；sortTypes=false 时保留传入顺序（壁纸分类的写死列表）。
 * 只列出 types 里出现过的分组与类型。
 */
export function buildTypeTree(types, { sortTypes = true } = {}) {
  const present = []
  ;(types || []).forEach((raw) => {
    const type = String(raw || '').trim()
    if (type && !present.includes(type)) present.push(type)
  })
  const groups = TYPE_GROUPS.filter((group) => group.types.some((type) => present.includes(type))).map((group) => ({
    value: GROUP_PREFIX + group.key,
    label: group.label,
    children: group.types.filter((type) => present.includes(type)).map((type) => ({ value: type, label: type })),
  }))
  if (present.includes(STICKER_TYPE)) {
    groups.push({ value: STICKER_TYPE, label: STICKER_TYPE, children: STICKER_KINDS.map((kind) => ({ ...kind })) })
  }
  const singles = present.filter((type) => type !== STICKER_TYPE && !groupOf(type))
  if (sortTypes) singles.sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }))
  return [...groups, ...singles.map((type) => ({ value: type, label: type }))]
}

/** 选中值 → { leaf } 或 { parent, items }；items 为空表示 All；空值或写坏的值返回 null。 */
export function parseTypeSelection(selection) {
  const value = String(selection || '').trim()
  if (!value) return null
  const mark = value.indexOf(ITEMS_MARK)
  if (mark < 0) {
    return isParent(value) ? { parent: value, items: [] } : { leaf: value }
  }
  const parent = value.slice(0, mark)
  if (!isParent(parent)) return null
  try {
    const items = JSON.parse(value.slice(mark + ITEMS_MARK.length))
    return Array.isArray(items) && items.length ? { parent, items: items.map(String) } : { parent, items: [] }
  } catch (e) {
    return null
  }
}

/** items 为空即该一级的 All。 */
export function formatTypeSelection(parent, items) {
  return items && items.length ? parent + ITEMS_MARK + JSON.stringify(items) : parent
}

/**
 * 右栏点一项后的新选中值。All 与其它项互斥；单选只留这一项；
 * 多选在同一个一级里增减，换到另一个一级就重新开始，减到一项不剩即不筛。
 */
export function selectTypeChild(selection, parent, child, multiple) {
  if (child === TYPE_ALL) return parent
  if (!multiple) return formatTypeSelection(parent, [child])
  const current = parseTypeSelection(selection)
  if (!current || current.parent !== parent || !current.items.length) return formatTypeSelection(parent, [child])
  const items = current.items.includes(child)
    ? current.items.filter((item) => item !== child)
    : [...current.items, child]
  return items.length ? formatTypeSelection(parent, items) : ''
}

/** 右栏某项是否处于选中；All 在该一级被整体选中时为真。 */
export function isTypeChildSelected(selection, parent, child) {
  const current = parseTypeSelection(selection)
  if (!current || current.parent !== parent) return false
  return child === TYPE_ALL ? !current.items.length : current.items.includes(child)
}

/** 左栏某项是否承载当前选中值（没有二级的类型本身被选，或其二级里有选中）。 */
export function isTypeNodeActive(selection, value) {
  const current = parseTypeSelection(selection)
  if (!current) return false
  return current.leaf === value || current.parent === value
}

/** 选中值 → 接口参数；空值返回空对象，表示不按类型筛。 */
export function toTypeQuery(selection) {
  const current = parseTypeSelection(selection)
  if (!current) return {}
  if (current.leaf) return { wallpaperType: current.leaf }
  if (current.parent === STICKER_TYPE) {
    return current.items.length
      ? { wallpaperType: STICKER_TYPE, stickerKind: current.items.join(',') }
      : { wallpaperType: STICKER_TYPE }
  }
  const types = current.items.length ? current.items : groupByParent(current.parent).types
  return { wallpaperTypes: types.join(',') }
}

/** 导出接口只认单个类型：多个类型不按类型筛，贴纸子类型退回全部贴纸。 */
export function toExportWallpaperType(selection) {
  const current = parseTypeSelection(selection)
  if (!current) return ''
  if (current.leaf) return current.leaf
  if (current.parent === STICKER_TYPE) return STICKER_TYPE
  return current.items.length === 1 ? current.items[0] : ''
}

/** 输入框与筛选摘要里的文案，如「食谱 / All」「stickers / static pack, animated solo」。 */
export function typeSelectionLabel(selection) {
  const current = parseTypeSelection(selection)
  if (!current) return String(selection || '').trim()
  if (current.leaf) return current.leaf
  const group = groupByParent(current.parent)
  const parentLabel = group ? group.label : STICKER_TYPE
  if (!current.items.length) return `${parentLabel} / All`
  const labels = current.items.map((item) => (current.parent === STICKER_TYPE ? stickerKindLabel(item) || item : item))
  return `${parentLabel} / ${labels.join(', ')}`
}
